import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Key, Loader2, Trash2 } from 'lucide-react'
import {
  deleteAppSecret,
  listAppSecrets,
  upsertAppSecret,
  type RequiredAppSecret,
} from '../lib/api'
import { ConfirmDialog } from './ui/ConfirmDialog'

// =============================================================================
// Props
// =============================================================================

interface AppSecretsSectionProps {
  appName: string
  /** Whether the current user can edit (owner or admin). When false, hides
   *  inputs and renders read-only catalog rows. */
  canManage: boolean
  /** Visual variant. 'card' adds the rounded-xl bordered container with a
   *  heading; 'embedded' is for when a parent (e.g. a workspace panel) already
   *  provides chrome. */
  variant?: 'card' | 'embedded'
}

// =============================================================================
// Component
// =============================================================================

/**
 * Unified App Secrets management UI.
 *
 * Renders the full catalog (configured + declared-but-empty rows) from the
 * `app_secrets` table. Empty rows are auto-expanded with a primary input
 * field; configured rows are collapsed and expand to reveal a replace/clear
 * UI. Each row supports three actions: Save/Replace value, Clear value (keep
 * row), Remove (delete row).
 *
 * Shared between AppDetail.tsx (the full-page secrets section) and the
 * workspace SecretsPanel.tsx so the two never diverge.
 */
export default function AppSecretsSection({
  appName,
  canManage,
  variant = 'card',
}: AppSecretsSectionProps) {
  const [appSecrets, setAppSecrets] = useState<RequiredAppSecret[]>([])
  const [secretsLoading, setSecretsLoading] = useState(false)
  const [secretSavingKey, setSecretSavingKey] = useState<string | null>(null)
  const [secretDeleting, setSecretDeleting] = useState<string | null>(null)
  const [secretClearing, setSecretClearing] = useState<string | null>(null)
  const [secretError, setSecretError] = useState<string | null>(null)
  const [secretSuccess, setSecretSuccess] = useState<string | null>(null)
  const [showAddSecretEditor, setShowAddSecretEditor] = useState(false)
  const [expandedSecrets, setExpandedSecrets] = useState<Set<string>>(new Set())
  const [secretValueDrafts, setSecretValueDrafts] = useState<Record<string, string>>({})
  const [secretNameInput, setSecretNameInput] = useState('')
  const [secretValueInput, setSecretValueInput] = useState('')
  const [secretDescriptionInput, setSecretDescriptionInput] = useState('')

  const loadSecrets = useCallback(async () => {
    if (!canManage) {
      setAppSecrets([])
      return
    }

    setSecretsLoading(true)
    setSecretError(null)
    try {
      const result = await listAppSecrets(appName)
      const rows = result.required_secrets || []
      setAppSecrets(rows)
      setExpandedSecrets(new Set(rows.filter((r) => !r.is_set).map((r) => r.name)))
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to load secrets')
    } finally {
      setSecretsLoading(false)
    }
  }, [appName, canManage])

  // Reset transient editor state when the app slug changes.
  useEffect(() => {
    setSecretSuccess(null)
    setSecretError(null)
    setSecretValueDrafts({})
    setShowAddSecretEditor(false)
    setSecretNameInput('')
    setSecretValueInput('')
    setSecretDescriptionInput('')
    setExpandedSecrets(new Set())
  }, [appName])

  useEffect(() => {
    loadSecrets()
  }, [loadSecrets])

  // Refresh on window focus so the workspace panel + App Details page stay in
  // sync after edits in the other.
  useEffect(() => {
    function onFocus() {
      loadSecrets()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [loadSecrets])

  function toggleSecretExpanded(name: string) {
    setExpandedSecrets((current) => {
      const next = new Set(current)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })
  }

  function setSecretValueDraft(secretName: string, value: string) {
    setSecretValueDrafts((current) => ({ ...current, [secretName]: value }))
  }

  async function handleAddNewSecret() {
    const key = secretNameInput.trim()
    if (!key) {
      setSecretError('Secret name is required')
      return
    }
    setSecretSavingKey('__new__')
    setSecretError(null)
    setSecretSuccess(null)
    try {
      await upsertAppSecret(appName, key, {
        value: secretValueInput || null,
        description: secretDescriptionInput.trim() || undefined,
      })
      setSecretSuccess(secretValueInput ? `"${key}" added` : `"${key}" declared (no value yet)`)
      setSecretNameInput('')
      setSecretValueInput('')
      setSecretDescriptionInput('')
      setShowAddSecretEditor(false)
      await loadSecrets()
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to save secret')
    } finally {
      setSecretSavingKey(null)
    }
  }

  async function handleSaveSecretValue(secret: RequiredAppSecret) {
    const draftValue = secretValueDrafts[secret.name]
    if (typeof draftValue !== 'string' || draftValue.length === 0) {
      setSecretError(`Enter a value for ${secret.display_name || secret.name}`)
      return
    }
    setSecretSavingKey(secret.name)
    setSecretError(null)
    setSecretSuccess(null)
    try {
      await upsertAppSecret(appName, secret.name, {
        value: draftValue,
        description: secret.description || undefined,
      })
      setSecretSuccess(`${secret.display_name || secret.name} saved`)
      setSecretValueDrafts((current) => ({ ...current, [secret.name]: '' }))
      setExpandedSecrets((current) => {
        const next = new Set(current)
        next.delete(secret.name)
        return next
      })
      await loadSecrets()
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to save secret')
    } finally {
      setSecretSavingKey(null)
    }
  }

  // ConfirmDialog (Radix AlertDialog) handles the confirmation flow; these
  // helpers run after the user clicks the "Clear" / "Remove" button in the
  // dialog. The previous `window.confirm()` opened the browser's native
  // dialog, which looks foreign and breaks keyboard styling.
  async function handleClearSecretValue(secret: RequiredAppSecret) {
    setSecretClearing(secret.name)
    setSecretError(null)
    setSecretSuccess(null)
    try {
      await upsertAppSecret(appName, secret.name, { value: null })
      setSecretSuccess(`${secret.display_name || secret.name} value cleared`)
      await loadSecrets()
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to clear secret')
    } finally {
      setSecretClearing(null)
    }
  }

  async function handleRemoveSecret(secret: RequiredAppSecret) {
    setSecretDeleting(secret.name)
    setSecretError(null)
    setSecretSuccess(null)
    try {
      await deleteAppSecret(appName, secret.name)
      setSecretSuccess(`"${secret.name}" removed`)
      await loadSecrets()
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to remove secret')
    } finally {
      setSecretDeleting(null)
    }
  }

  if (!canManage) {
    return null
  }

  const body = (
    <>
      <div className="flex items-start justify-between gap-3 mb-2">
        {variant === 'card' ? (
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" />
            App secrets
          </h2>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => setShowAddSecretEditor((current) => !current)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-secondary transition-colors"
        >
          {showAddSecretEditor ? (
            <>
              <ChevronDown className="w-3 h-3" />
              Cancel
            </>
          ) : (
            <>
              <ChevronRight className="w-3 h-3" />
              Add secret
            </>
          )}
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Every configured value is auto-injected into the container's environment by name at deploy.
        Values are write-only and never shown again. Missing rows leave the env var unset — your
        app's guards take over.
      </p>

      {secretError && (
        <div className="mb-3 p-3 rounded-lg bg-destructive/10 text-destructive text-sm border border-destructive/20">
          {secretError}
        </div>
      )}
      {secretSuccess && (
        <div className="mb-3 p-3 rounded-lg bg-green-500/10 text-green-500 text-sm border border-green-500/20">
          {secretSuccess}
        </div>
      )}

      {showAddSecretEditor && (
        <div className="mb-4 p-3 rounded-lg border border-border bg-muted/30 space-y-2">
          <input
            type="text"
            value={secretNameInput}
            onChange={(e) => setSecretNameInput(e.target.value)}
            placeholder="Secret name (e.g. OPENAI_API_KEY)"
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <input
            type="password"
            value={secretValueInput}
            onChange={(e) => setSecretValueInput(e.target.value)}
            placeholder="Value (leave empty to declare-only)"
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <input
            type="text"
            value={secretDescriptionInput}
            onChange={(e) => setSecretDescriptionInput(e.target.value)}
            placeholder="Description (optional)"
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            onClick={handleAddNewSecret}
            disabled={secretSavingKey === '__new__'}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {secretSavingKey === '__new__' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Key className="w-4 h-4" />
            )}
            {secretValueInput ? 'Save' : 'Declare'}
          </button>
        </div>
      )}

      {secretsLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading secrets...
        </div>
      ) : appSecrets.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No app secrets yet. Click <em>Add secret</em> above, or import a repo with an
          <code className="ml-1 font-mono">.env.example</code>.
        </p>
      ) : (
        <div className="space-y-2">
          {appSecrets.map((secret) => {
            const expanded = expandedSecrets.has(secret.name)
            const saving = secretSavingKey === secret.name
            const clearing = secretClearing === secret.name
            const removing = secretDeleting === secret.name
            const isFile = secret.kind === 'file'
            return (
              <div key={secret.name} className="rounded-lg border border-border">
                <button
                  type="button"
                  onClick={() => toggleSecretExpanded(secret.name)}
                  className="w-full flex items-start justify-between gap-3 p-3 text-left hover:bg-secondary/40 transition-colors rounded-lg"
                >
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    {expanded ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-foreground break-all">{secret.name}</p>
                      {secret.display_name && secret.display_name !== secret.name && (
                        <p className="text-xs text-muted-foreground mt-0.5">{secret.display_name}</p>
                      )}
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 rounded-md text-[11px] border shrink-0 ${
                      secret.is_set
                        ? 'border-green-500/30 text-green-500 bg-green-500/10'
                        : 'border-amber-500/30 text-amber-500 bg-amber-500/10'
                    }`}
                  >
                    {secret.is_set ? 'Configured' : 'Empty'}
                  </span>
                </button>

                {expanded && (
                  <div className="px-3 pb-3 space-y-2 border-t border-border/60">
                    {secret.description && (
                      <p className="text-xs text-muted-foreground mt-3">{secret.description}</p>
                    )}
                    {(secret.source || (secret.referenced_by && secret.referenced_by.length > 0) || isFile) && (
                      <div className="text-[11px] text-muted-foreground space-y-0.5 mt-2">
                        {secret.source && <p>Source: {secret.source}</p>}
                        {isFile && secret.mount_path && <p>Mounted as a file at {secret.mount_path}</p>}
                        {secret.referenced_by && secret.referenced_by.length > 0 && (
                          <p className="break-all">Referenced by {secret.referenced_by.join(', ')}</p>
                        )}
                      </div>
                    )}

                    {isFile ? (
                      <textarea
                        value={secretValueDrafts[secret.name] || ''}
                        onChange={(e) => setSecretValueDraft(secret.name, e.target.value)}
                        rows={4}
                        spellCheck={false}
                        placeholder={secret.is_set ? 'Paste replacement file contents' : 'Paste file contents'}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    ) : (
                      <input
                        type="password"
                        value={secretValueDrafts[secret.name] || ''}
                        onChange={(e) => setSecretValueDraft(secret.name, e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={secret.is_set ? 'Enter replacement value' : 'Enter value'}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    )}
                    <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
                      {secret.is_set && (
                        <ConfirmDialog
                          trigger={
                            <button
                              type="button"
                              disabled={clearing || saving || removing}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs hover:bg-secondary transition-colors disabled:opacity-50"
                              title="Clear the value; keep the row in the catalog"
                            >
                              {clearing && <Loader2 className="w-3 h-3 animate-spin" />}
                              Clear value
                            </button>
                          }
                          title={`Clear "${secret.name}"?`}
                          description="The row stays in the catalog so the input remains visible — only the stored value is removed. The container's env var will be unset on the next deploy."
                          confirmLabel="Clear"
                          loadingLabel="Clearing..."
                          variant="destructive"
                          onConfirm={() => handleClearSecretValue(secret)}
                        />
                      )}
                      <ConfirmDialog
                        trigger={
                          <button
                            type="button"
                            disabled={clearing || saving || removing}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-destructive/30 text-destructive text-xs hover:bg-destructive/10 transition-colors disabled:opacity-50"
                            title="Delete the row from the catalog entirely"
                          >
                            {removing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            Remove
                          </button>
                        }
                        title={`Remove "${secret.name}"?`}
                        description="Deletes the row from the secret catalog entirely. The importer's hint disappears; if this secret is referenced by the manifest, the next deploy will simply leave the env var unset."
                        confirmLabel="Remove"
                        loadingLabel="Removing..."
                        variant="destructive"
                        onConfirm={() => handleRemoveSecret(secret)}
                      />
                      <button
                        type="button"
                        onClick={() => handleSaveSecretValue(secret)}
                        disabled={saving || clearing || removing}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs hover:bg-primary/90 transition-colors disabled:opacity-50"
                      >
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Key className="w-3 h-3" />}
                        {secret.is_set ? 'Replace value' : 'Save value'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )

  if (variant === 'embedded') {
    return <div className="p-4">{body}</div>
  }
  return <div className="p-6 rounded-xl border border-border bg-card">{body}</div>
}
